// src/pages/Emirates/Emirates.jsx
import React, { useState, useEffect, useCallback } from 'react';
import Header from '../../components/layout/Header/Header';
import Sidebar from '../../components/layout/Sidebar/Sidebar';
import TableScrollSync from "../../components/common/TableScrollSync";
import './Emirates.css';
import axios from 'axios';
import toast from "../../utils/toast";
import { useNavigate } from 'react-router-dom';
import { useAppSettings } from '../../context/AppSettingsContext';
import { usePaginatedData } from '../../hooks/usePagination';
import Pagination from '../../components/common/Pagination';

const Emirates = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [emiratesList, setEmiratesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const { entriesPerPage } = useAppSettings();
  const pagination = usePaginatedData(emiratesList, entriesPerPage, '');

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingEmirates, setEditingEmirates] = useState(null);
  const [editFormData, setEditFormData] = useState({ emiratesName: '', emiratesCode: '' });
  const [editErrors, setEditErrors] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const backendUrl = process.env.REACT_APP_BACKEND_IP;

  const fetchCurrentUser = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { window.location.href = '/login'; return; }
      const response = await axios.get(`${backendUrl}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(response.data.user || response.data);
    } catch {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
  }, [backendUrl]);

  const fetchEmirates = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${backendUrl}/api/emirates/getAll`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setEmiratesList(response.data);
    } catch {
      toast.error('Failed to load emirates list');
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  useEffect(() => {
    fetchCurrentUser();
    fetchEmirates();
  }, [fetchCurrentUser, fetchEmirates]);

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${backendUrl}/api/emirates/delete/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`"${name}" deleted successfully`);
      fetchEmirates();
    } catch {
      toast.error('Failed to delete Emirates');
    }
  };

  const handleEditClick = (emirates) => {
    setEditingEmirates(emirates);
    setEditFormData({ emiratesName: emirates.emiratesName, emiratesCode: emirates.emiratesCode });
    setEditErrors({});
    setShowEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
    if (editErrors[name]) setEditErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleEditSave = async () => {
    const errors = {};
    if (!editFormData.emiratesName.trim()) errors.emiratesName = 'Emirates name is required';
    if (!editFormData.emiratesCode.trim()) errors.emiratesCode = 'Emirates code is required';
    if (Object.keys(errors).length > 0) { setEditErrors(errors); return; }

    setEditSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${backendUrl}/api/emirates/update/${editingEmirates._id}`,
        { emiratesName: editFormData.emiratesName.trim(), emiratesCode: editFormData.emiratesCode.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Emirates updated successfully!');
      setShowEditModal(false);
      setEditingEmirates(null);
      fetchEmirates();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update Emirates');
    } finally {
      setEditSaving(false);
    }
  };

  if (!user) return <div className="emirates-loading">Loading...</div>;

  return (
    <div className="emirates-layout">
      <Header
        sidebarOpen={sidebarOpen}
        onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
        user={user}
      />
      <Sidebar
        isOpen={sidebarOpen}
        activeItem="Emirates"
        onSetActiveItem={() => {}}
        onClose={() => setSidebarOpen(false)}
        user={user}
      />

      <main className={`emirates-main-content ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="emirates-page-wrapper">

          {/* â”€â”€ List Card â”€â”€ */}
          <div className="emirates-list-card">
            <div className="emirates-list-header">
              <div>
                <h2>Emirates List</h2>
                <p className="emirates-list-sub">Manage all emirates regions</p>
              </div>
              <button
                className="emirates-add-btn"
                onClick={() => navigate('/emirates/create')}
              >
                + Add Emirates
              </button>
            </div>

            {loading ? (
              <div className="emirates-loading">Loading emirates...</div>
            ) : emiratesList.length === 0 ? (
              <div className="emirates-no-data">No emirates found. Click "Add Emirates" to create one.</div>
            ) : (
              <>
                <TableScrollSync>
                  <div className="emirates-table-wrapper">
                    <table className="emirates-data-table">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Emirates Name</th>
                          <th>Emirates Code</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagination.pageData.map((em, index) => (
                          <tr key={em._id}>
                            <td>{pagination.showingFrom + index}</td>
                            <td className="emirates-name-cell">{em.emiratesName}</td>
                            <td>
                              <span className="emirates-code-badge">{em.emiratesCode}</span>
                            </td>
                            <td>
                              <div className="emirates-actions-cell">
                                <button className="emirates-edit-btn" onClick={() => handleEditClick(em)}>
                                  Edit
                                </button>
                                <button className="emirates-delete-btn" onClick={() => handleDelete(em._id, em.emiratesName)}>
                                  Delete
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </TableScrollSync>

                <Pagination
                  page={pagination.page}
                  totalPages={pagination.totalPages}
                  totalRecords={pagination.totalRecords}
                  showingFrom={pagination.showingFrom}
                  showingTo={pagination.showingTo}
                  canPrev={pagination.canPrev}
                  canNext={pagination.canNext}
                  onPrev={pagination.goPrev}
                  onNext={pagination.goNext}
                />
              </>
            )}
          </div>
        </div>
      </main>

      {/* â”€â”€ Edit Modal â”€â”€ */}
      {showEditModal && (
        <div className="emirates-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="emirates-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Edit Emirates</h3>

            <div className="emirates-form-group">
              <label htmlFor="edit-emiratesName">Emirates Name</label>
              <input
                id="edit-emiratesName"
                name="emiratesName"
                type="text"
                value={editFormData.emiratesName}
                onChange={handleEditChange}
                className={editErrors.emiratesName ? 'input-error' : ''}
              />
              {editErrors.emiratesName && (
                <span className="emirates-error-text">{editErrors.emiratesName}</span>
              )}
            </div>

            <div className="emirates-form-group">
              <label htmlFor="edit-emiratesCode">Emirates Code</label>
              <input
                id="edit-emiratesCode"
                name="emiratesCode"
                type="text"
                value={editFormData.emiratesCode}
                onChange={handleEditChange}
                className={editErrors.emiratesCode ? 'input-error' : ''}
              />
              {editErrors.emiratesCode && (
                <span className="emirates-error-text">{editErrors.emiratesCode}</span>
              )}
            </div>

            <div className="emirates-modal-actions">
              <button className="emirates-cancel-btn" onClick={() => setShowEditModal(false)} disabled={editSaving}>
                Cancel
              </button>
              <button className="emirates-submit-btn" onClick={handleEditSave} disabled={editSaving}>
                {editSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Emirates;

